import { useEffect } from 'react';
import { useRoutes } from 'react-router-dom';
import router from 'src/router';
import { useDispatch, useSelector } from 'src/store';
import { connectToSocket, disconnectFromSocket } from 'src/slices/socket';
import { requestGetShopData } from 'src/slices/settings';
import { markStartup, measureStartup } from 'src/utils/startupPerformance';

import { SnackbarProvider } from 'notistack';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import useAuth from 'src/hooks/useAuth';

import { CssBaseline, GlobalStyles } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import AppInit from './components/AppInit';
import AccountProvisioning from './components/AccountProvisioning';
import AccountContextUnavailable from './components/AccountContextUnavailable';

function App() {
  const content = useRoutes(router);
  const auth = useAuth();

  const dispatch = useDispatch();
  const { connectionStatus, connectedShopId, targetShopId } = useSelector((state) => state.socket);
  const activeShopId = Number(auth.user?.shop?.id || 0);
  const hasOperationalAccount = Boolean(
    auth.isInitialized &&
    auth.isAuthenticated &&
    activeShopId &&
    !auth.needsProvisioning &&
    !auth.accountError
  );
  const socketMatchesActiveShop = connectionStatus === 'connected' && Number(connectedShopId) === activeShopId;
  const socketFailedForActiveShop = connectionStatus === 'connection failed' && Number(targetShopId) === activeShopId;
  const isWaitingForSocket = hasOperationalAccount && !socketMatchesActiveShop && !socketFailedForActiveShop;

  useEffect(() => {
    if (!auth.isInitialized) {
      return undefined;
    }

    if (hasOperationalAccount) {
      dispatch(connectToSocket({ shopId: activeShopId }));
    } else {
      dispatch(disconnectFromSocket());
    }
    return undefined;
  }, [
    auth.isInitialized,
    auth.sessionRevision,
    activeShopId,
    dispatch,
    hasOperationalAccount
  ]);

  useEffect(() => {
    if (!socketMatchesActiveShop) return;
    markStartup('application-ready');
    measureStartup('authenticated-application-ready', 'script-ready', 'application-ready');
    dispatch(requestGetShopData());
  }, [dispatch, socketMatchesActiveShop]);

  useEffect(() => () => {
    dispatch(disconnectFromSocket());
  }, [dispatch]);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SnackbarProvider
          maxSnack={6}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
        >
          <CssBaseline />
          <GlobalStyles
            styles={(theme) => ({
              '*': {
                scrollbarWidth: 'thin',
                scrollbarColor: `${theme.palette.primary.light} transparent`
              },
              '*::-webkit-scrollbar': {
                width: 10,
                height: 10
              },
              '*::-webkit-scrollbar-track': {
                background: 'transparent'
              },
              '*::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.primary.light,
                borderRadius: 999,
                border: `3px solid ${theme.palette.background.default}`
              },
              '*::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.primary.main
              },
              '*::-webkit-scrollbar-corner': {
                background: 'transparent'
              }
            })}
          />
          {!auth.isInitialized && <AppInit />}
          {auth.isInitialized && auth.isAuthenticated && auth.needsProvisioning && <AccountProvisioning />}
          {auth.isInitialized && auth.isAuthenticated && !auth.needsProvisioning && auth.accountError && (
            <AccountContextUnavailable />
          )}
          {isWaitingForSocket && <AppInit />}
          {auth.isInitialized && !isWaitingForSocket && (!auth.isAuthenticated || (!auth.needsProvisioning && !auth.accountError)) && content}
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
export default App;
